/**
 * Level-matching engine (spec 17).
 *
 * Comparing two pieces of hardware at different loudness is not a comparison of
 * hardware, it is a comparison of loudness -- and the louder one wins almost
 * every time. So the compensation is measured, not guessed: a few seconds of
 * the ACTUAL source material is rendered through each complete chain offline
 * and the result is measured with the chosen metric.
 *
 * The exact compensation is always displayed, and matching can always be turned
 * off, because a user comparing a 105 dB horn against an 86 dB standmount may
 * legitimately want to hear the level difference.
 */

import type { AmpProfile, DacProfile, OutputProfile } from '../types/hardware';
import type { EngineSettings, LevelMatchMode, SystemChain, SystemSlotKey } from '../types/system';
import { renderOffline } from '../render/offline';

export interface ChainLevel {
  peakDbFS: number;
  rmsDbFS: number;
  lufs: number;
  /** Seconds of material measured. */
  seconds: number;
}

export interface LevelMatchResult {
  a: ChainLevel;
  b: ChainLevel;
  /** Trim to apply to each system, in dB. */
  trimA: number;
  trimB: number;
  mode: LevelMatchMode;
  /** Human-readable summary, e.g. "System B: -0.72 dB". */
  summary: string;
  measuredAt: number;
}

export interface LevelProbeRequest {
  buffer: AudioBuffer;
  chain: SystemChain;
  dac: DacProfile | null;
  amp: AmpProfile | null;
  output: OutputProfile | null;
  engine: EngineSettings;
  sourceRate: number;
  /** Where in the file to sample from. Defaults to a point 1/3 in. */
  atSec?: number;
  seconds?: number;
  applySensitivity?: boolean;
  referenceSpl?: number;
  /** Cable resolver, so level matching measures the cabled chain. */
  resolveCable?: (id: string | null) => import('../types/hardware').CableProfile | null;
}

const K_SHELF = (fs: number) => {
  const f0 = 1681.974450955533;
  const G = 3.999843853973347;
  const Q = 0.7071752369554196;
  const K = Math.tan((Math.PI * f0) / fs);
  const Vh = Math.pow(10, G / 20);
  const Vb = Math.pow(Vh, 0.4996667741545416);
  const a0 = 1 + K / Q + K * K;
  return {
    b0: (Vh + (Vb * K) / Q + K * K) / a0,
    b1: (2 * (K * K - Vh)) / a0,
    b2: (Vh - (Vb * K) / Q + K * K) / a0,
    a1: (2 * (K * K - 1)) / a0,
    a2: (1 - K / Q + K * K) / a0,
  };
};

const K_HP = (fs: number) => {
  const f0 = 38.13547087602444;
  const Q = 0.5003270373238773;
  const K = Math.tan((Math.PI * f0) / fs);
  const den = 1 + K / Q + K * K;
  return { b0: 1, b1: -2, b2: 1, a1: (2 * (K * K - 1)) / den, a2: (1 - K / Q + K * K) / den };
};

function biquadInPlace(x: Float32Array, c: ReturnType<typeof K_HP>): void {
  let z1 = 0;
  let z2 = 0;
  for (let i = 0; i < x.length; i++) {
    const v = x[i];
    const y = c.b0 * v + z1;
    z1 = c.b1 * v - c.a1 * y + z2;
    z2 = c.b2 * v - c.a2 * y;
    x[i] = y;
  }
}

export function measureLevel(channels: Float32Array[], sampleRate: number): ChainLevel {
  const n = channels[0]?.length ?? 0;
  if (!n) return { peakDbFS: -Infinity, rmsDbFS: -Infinity, lufs: -Infinity, seconds: 0 };

  let peak = 0;
  let ss = 0;
  for (const ch of channels) {
    for (let i = 0; i < ch.length; i++) {
      const a = Math.abs(ch[i]);
      if (a > peak) peak = a;
      ss += ch[i] * ch[i];
    }
  }
  const rms = Math.sqrt(ss / (n * channels.length));

  // K-weighted mean square across channels.
  const shelf = K_SHELF(sampleRate);
  const hp = K_HP(sampleRate);
  let power = 0;
  for (const ch of channels) {
    const copy = Float32Array.from(ch);
    biquadInPlace(copy, shelf);
    biquadInPlace(copy, hp);
    let s = 0;
    for (let i = 0; i < copy.length; i++) s += copy[i] * copy[i];
    power += s / copy.length;
  }

  return {
    peakDbFS: 20 * Math.log10(Math.max(1e-9, peak)),
    rmsDbFS: 20 * Math.log10(Math.max(1e-9, rms)),
    lufs: -0.691 + 10 * Math.log10(Math.max(1e-12, power)),
    seconds: n / sampleRate,
  };
}

/** Render a short excerpt through a chain and measure what comes out. */
export async function probeChainLevel(req: LevelProbeRequest): Promise<ChainLevel> {
  const seconds = req.seconds ?? 4;
  const at = req.atSec ?? Math.max(0, Math.min(req.buffer.duration * 0.33, req.buffer.duration - seconds));
  const end = Math.min(req.buffer.duration, at + seconds);
  if (end <= at) {
    return { peakDbFS: -Infinity, rmsDbFS: -Infinity, lufs: -Infinity, seconds: 0 };
  }

  const result = await renderOffline({
    buffer: req.buffer,
    // Room off (it adds decay, not level identity); cabling left intact,
    // because a speaker cable genuinely changes level and that is exactly what
    // level matching exists to compensate for.
    chain: { ...req.chain, room: { ...req.chain.room, enabled: false } },
    dac: req.dac,
    amp: req.amp,
    output: req.output,
    // Probe at balanced quality: level does not need maximum-quality DSP, and
    // a level probe that takes ten seconds is a level probe nobody waits for.
    engine: { ...req.engine, realism: 'standard', performance: 'balanced', safetyLimiter: false },
    sourceRate: req.sourceRate,
    startSec: at,
    endSec: end,
    targetRate: Math.min(48000, req.buffer.sampleRate),
    masterGainDb: 0,
    limiter: false,
    applySensitivity: req.applySensitivity,
    referenceSpl: req.referenceSpl,
    resolveCable: req.resolveCable,
  });

  return measureLevel(result.channels, result.sampleRate);
}

const pick = (l: ChainLevel, mode: LevelMatchMode): number => {
  switch (mode) {
    case 'peak': return l.peakDbFS;
    case 'lufs': return l.lufs;
    case 'rms':
    default: return l.rmsDbFS;
  }
};

/**
 * Compute the trims that bring both systems to the same measured level.
 *
 * The quieter system is left alone and the louder one is brought down, so
 * matching can never push a chain into clipping.
 */
export function computeTrims(a: ChainLevel, b: ChainLevel, mode: LevelMatchMode): LevelMatchResult {
  if (mode === 'off') {
    return {
      a, b, trimA: 0, trimB: 0, mode,
      summary: 'Level matching off. Systems play at their natural relative level.',
      measuredAt: Date.now(),
    };
  }
  const la = pick(a, mode);
  const lb = pick(b, mode);
  if (!Number.isFinite(la) || !Number.isFinite(lb)) {
    return {
      a, b, trimA: 0, trimB: 0, mode,
      summary: 'Level could not be measured (one chain produced silence).',
      measuredAt: Date.now(),
    };
  }
  const target = Math.min(la, lb);
  const trimA = target - la;
  const trimB = target - lb;
  const label = mode === 'lufs' ? 'LUFS' : mode === 'peak' ? 'peak' : 'RMS';
  const parts: string[] = [];
  if (Math.abs(trimA) > 0.005) parts.push(`System A: ${trimA >= 0 ? '+' : ''}${trimA.toFixed(2)} dB`);
  if (Math.abs(trimB) > 0.005) parts.push(`System B: ${trimB >= 0 ? '+' : ''}${trimB.toFixed(2)} dB`);
  return {
    a, b, trimA, trimB, mode,
    summary: parts.length
      ? `Matched on ${label}: ${parts.join(', ')}`
      : `Already matched on ${label} (difference below 0.01 dB)`,
    measuredAt: Date.now(),
  };
}

export interface MatchBothRequest {
  buffer: AudioBuffer;
  chains: Record<SystemSlotKey, LevelProbeRequest>;
  mode: LevelMatchMode;
  manualTrimA: number;
  manualTrimB: number;
}

export async function matchSystems(req: MatchBothRequest): Promise<LevelMatchResult> {
  if (req.mode === 'off') {
    return computeTrims(
      { peakDbFS: 0, rmsDbFS: 0, lufs: 0, seconds: 0 },
      { peakDbFS: 0, rmsDbFS: 0, lufs: 0, seconds: 0 },
      'off',
    );
  }
  const [a, b] = await Promise.all([
    probeChainLevel(req.chains.A),
    probeChainLevel(req.chains.B),
  ]);
  const r = computeTrims(a, b, req.mode);
  return {
    ...r,
    trimA: r.trimA + req.manualTrimA,
    trimB: r.trimB + req.manualTrimB,
  };
}
